/**
 * Sekcje ekranu Ustawień jako pozycje globalnej wyszukiwarki (Ctrl+K) — zgłoszenie ze szkolenia
 * 14.10: wyszukiwarka nie znajdowała „Kreatora menu", „Słowników", „Centrum uprawnień" itd.
 *
 * Kolejność, etykiety i trasy = pozycje nawigacji w `pages/Settings.tsx` (grupy `groups`).
 * Dodając/usuwając sekcję tam, zaktualizuj tę listę. Bramki widoczności NIE są tu powielane —
 * `requires` wskazuje flagę z `useSettingsSectionAccess` (to samo źródło co ekran Ustawień).
 */

import type { SettingsSectionAccess } from '@/hooks/useSettingsSectionAccess';

export interface SettingsSearchEntry {
  /** Id sekcji w `pages/Settings.tsx`. */
  id: string;
  labelKey: string;
  /** Trasa: `/settings/<id>` albo własna trasa pozycji-linku (kreator menu, procesy, pulpit). */
  path: string;
  /** Flaga dostępu; brak = sekcja widoczna dla każdego, kto wchodzi w Ustawienia. */
  requires?: keyof SettingsSectionAccess;
}

export const SETTINGS_SEARCH_ENTRIES: readonly SettingsSearchEntry[] = [
  // Strona startowa Ustawień (profil osadzony); sam „Profil" dokłada wyszukiwarka jako /profile.
  { id: 'profile', labelKey: 'tenant.sidebar.settings', path: '/settings' },
  {
    id: 'sidebar-rail',
    labelKey: 'tenant.settings.tabs.sidebarRail',
    path: '/settings/sidebar-rail',
  },
  {
    id: 'general',
    labelKey: 'tenant.settings.tabs.general',
    path: '/settings/general',
  },
  {
    id: 'permissions',
    labelKey: 'tenant.settings.tabs.permissions',
    path: '/settings/permissions',
  },
  {
    id: 'permission-center',
    labelKey: 'tenant.settings.tabs.permissionCenter',
    path: '/settings/permission-center',
  },
  {
    id: 'org-structure',
    labelKey: 'tenant.settings.tabs.orgStructure',
    path: '/settings/org-structure',
  },
  {
    id: 'dictionaries',
    labelKey: 'tenant.settings.tabs.dictionaries',
    path: '/settings/dictionaries',
  },
  {
    id: 'non-working-days',
    labelKey: 'tenant.settings.tabs.nonWorkingDays',
    path: '/settings/non-working-days',
    requires: 'canManageNonWorkingDays',
  },
  {
    id: 'record-link-types',
    labelKey: 'tenant.settings.tabs.recordLinkTypes',
    path: '/settings/record-link-types',
  },
  {
    id: 'widgets',
    labelKey: 'tenant.settings.tabs.widgets',
    path: '/settings/widgets',
    requires: 'canManageWidgets',
  },
  {
    id: 'default-dashboard',
    labelKey: 'tenant.settings.tabs.defaultDashboard',
    path: '/dashboard/default',
    requires: 'canManageDefaultDashboard',
  },
  {
    id: 'document-types',
    labelKey: 'tenant.settings.tabs.documentTypes',
    path: '/settings/document-types',
  },
  { id: 'task-types', labelKey: 'tenant.settings.tabs.taskTypes', path: '/settings/task-types' },
  {
    id: 'record-categories',
    labelKey: 'tenant.settings.tabs.recordCategories',
    path: '/settings/record-categories',
    requires: 'canManageRecordCategories',
  },
  { id: 'menu-builder', labelKey: 'tenant.settings.tabs.menuBuilder', path: '/menu-builder' },
  {
    id: 'process-definitions',
    labelKey: 'tenant.settings.tabs.processDefinitions',
    path: '/processes',
    requires: 'canManageProcessDefinitions',
  },
  {
    id: 'sql-procedures',
    labelKey: 'tenant.settings.tabs.sqlProcedures',
    path: '/settings/sql-procedures',
    requires: 'canManageSqlProcedures',
  },
  {
    id: 'list-actions',
    labelKey: 'tenant.settings.tabs.listActions',
    path: '/settings/list-actions',
    requires: 'canManageListActions',
  },
  {
    id: 'chart-of-accounts',
    labelKey: 'tenant.settings.tabs.chartOfAccounts',
    path: '/settings/chart-of-accounts',
  },
  {
    id: 'decree-templates',
    labelKey: 'tenant.settings.tabs.decreeTemplates',
    path: '/settings/decree-templates',
  },
  {
    id: 'decree-procedures',
    labelKey: 'decreeProcedures.tabLabel',
    path: '/settings/decree-procedures',
  },
  { id: 'modules', labelKey: 'tenant.settings.tabs.modules', path: '/settings/modules' },
  {
    id: 'integrations',
    labelKey: 'tenant.settings.tabs.integrations',
    path: '/settings/integrations',
  },
  { id: 'ksef', labelKey: 'tenant.settings.tabs.ksef', path: '/settings/ksef' },
  {
    id: 'modules-overview',
    labelKey: 'tenant.settings.tabs.modulesOverview',
    path: '/settings/modules-overview',
  },
  {
    id: 'module-fields',
    labelKey: 'tenant.settings.tabs.moduleFields',
    path: '/settings/module-fields',
  },
  {
    id: 'additional-tables',
    labelKey: 'additionalTables.admin.tabLabel',
    path: '/settings/additional-tables',
    requires: 'canManageCustomModules',
  },
  {
    id: 'additional-views',
    labelKey: 'additionalViews.admin.tabLabel',
    path: '/settings/additional-views',
    requires: 'canManageAdditionalViews',
  },
  {
    id: 'billing',
    labelKey: 'tenant.settings.tabs.billing',
    path: '/settings/billing',
    requires: 'isSuperUser',
  },
];

/** Sekcje widoczne dla usera wg tych samych bramek co ekran Ustawień. */
export function visibleSettingsSearchEntries(access: SettingsSectionAccess): SettingsSearchEntry[] {
  return SETTINGS_SEARCH_ENTRIES.filter((e) => !e.requires || access[e.requires]);
}
